/**
 * Shared validation and formatting utilities for forms
 */

// ============== EMAIL ==============

/**
 * Validates email address format
 */
export function isValidEmail(email: string): boolean {
  if (!email.trim()) return true; // Empty is ok (optional)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Returns error message for invalid email
 */
export function getEmailErrorMessage(email: string): string | undefined {
  if (!email.trim()) return undefined;
  if (!isValidEmail(email)) {
    if (email.split('@').length > 2) {
      return 'Nur ein @-Zeichen erlaubt';
    }
    if (!email.includes('@')) {
      return '@-Zeichen fehlt';
    }
    if (!email.includes('.')) {
      return 'Domain muss einen Punkt enthalten';
    }
    return 'Ungültige E-Mail-Adresse';
  }
  return undefined;
}

// ============== PHONE ==============

/**
 * Extract only digits from string
 */
export function extractDigits(value: string): string {
  return value.replace(/\D/g, '');
}

// German mobile prefixes
const MOBILE_PREFIXES = ['015', '016', '017'];

/**
 * Check if number is a mobile number
 */
export function isMobileNumber(digits: string): boolean {
  return MOBILE_PREFIXES.some(prefix => digits.startsWith(prefix));
}

/**
 * Normalize and format phone number
 * - Converts +49, 0049, 49 to 0
 * - Formats as standard German format
 * - Handles any garbage input
 */
export function normalizePhoneNumber(input: string): string {
  if (!input.trim()) return '';

  // Remove everything except digits and +
  let value = input.replace(/[^\d+]/g, '');

  // Convert international prefixes to German 0
  if (value.startsWith('+49')) {
    value = '0' + value.slice(3);
  } else if (value.startsWith('0049')) {
    value = '0' + value.slice(4);
  } else if (value.startsWith('49') && value.length > 10) {
    // 49170... → 0170...
    value = '0' + value.slice(2);
  } else if (value.startsWith('+')) {
    // Other international: + → 00
    value = '00' + value.slice(1);
  }

  // If no leading 0, add it if it looks like a mobile number
  if (!value.startsWith('0') && value.length >= 9) {
    if (/^1[567]/.test(value)) {
      value = '0' + value;
    }
  }

  const digits = value;

  if (digits.length < 6) {
    return digits; // Too short to format
  }

  // Mobile: 0XXX XXX XXXX (4 + 3 + rest)
  if (isMobileNumber(digits)) {
    if (digits.length >= 11) {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  }

  // Landline: 2-digit area code (030, 040, 069, 089)
  if (/^0[2-9]0/.test(digits)) {
    return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  }

  // Landline: 3-digit area code (0XXX)
  if (/^0[2-9]/.test(digits)) {
    return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  }

  // International (00XX...)
  if (digits.startsWith('00')) {
    return `+${digits.slice(2, 4)} ${digits.slice(4)}`;
  }

  return digits;
}

/**
 * Validates if phone number is valid
 */
export function isValidPhone(phone: string): boolean {
  if (!phone.trim()) return true; // Empty is ok (optional)

  const digits = extractDigits(phone);

  // At least 3 digits (short numbers, extensions)
  if (digits.length < 3) return false;

  // Max 15 digits (international standard)
  if (digits.length > 15) return false;

  return true;
}

/**
 * Returns error message for invalid phone number with helpful feedback
 */
export function getPhoneErrorMessage(phone: string): string | undefined {
  if (!phone.trim()) return undefined;

  const digits = extractDigits(phone);

  if (digits.length < 3) {
    return 'Zu kurz – mindestens 3 Ziffern';
  }

  if (digits.length > 15) {
    return 'Zu lang – maximal 15 Ziffern';
  }

  return undefined;
}

/**
 * Filter input to only allow phone characters
 */
export function filterPhoneInput(value: string): string {
  return value.replace(/[^\d\s\-+()]/g, '');
}

// ============== FORM HELPERS ==============

/**
 * Check if contact has at least one contact method
 */
export function hasContactMethod(
  email?: string,
  phone?: string,
  mobile?: string
): boolean {
  return !!(email?.trim() || phone?.trim() || mobile?.trim());
}

/**
 * Format API error response for display
 */
export function formatApiError(error: unknown): string {
  if (!error) return '';

  const err = error as {
    response?: {
      data?: {
        detail?: string | Array<{ msg: string; loc: string[] }>;
      };
    };
    message?: string;
  };

  const detail = err?.response?.data?.detail;

  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        // Make field names more readable
        const field = (d.loc ?? [])
          .filter((l) => l !== 'body')
          .join(' → ')
          .replace(/(\d+)/, (_, n) => `#${parseInt(n) + 1}`);
        // Pydantic stellt eigenen Validator-Meldungen "Value error, " voran;
        // das ist fuer den Nutzer Rauschen. Ein Fehler am ganzen Objekt
        // (model_validator, loc = ["body"]) hat kein Feld — dann nur die Meldung.
        const msg = d.msg.replace(/^Value error, /, '');
        return field ? `${field}: ${msg}` : msg;
      })
      .join('\n');
  }

  return err?.message || 'Ein unbekannter Fehler ist aufgetreten';
}
